import { ReactNode, useContext } from "react";
import { isEmpty, omit } from "lodash";
import { useDropzone as useReactDropzone } from "react-dropzone";
import { Control, FieldValues, useForm } from "react-hook-form";
import { RESOURCE_NAME } from "common/authorization";
import { MULTIPART_UPLOAD_TYPE } from "common/constants";
import { DATUM_DISPLAY } from "common/utils/datums";
import {
  CoastalADetermination,
  DatumConversionOffsetPurpose,
  GetAccountInfoQuery,
  useGetAccountInfoQuery,
  useUpdateAccountLogoMutation,
  useUpdateAccountMutation,
} from "../../../generated/graphql";
import { useMultipartFileUpload } from "../../../hooks/useMultipartFileUpload";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { FlexColumn, FlexRow } from "../../Common/__styles__/Layout";
import { SettingsForm } from "../../Common/__styles__/Settings";
import { AccountLogoOrFallbackIcon } from "../../Common/AccountLogo";
import { ButtonLink } from "../../Common/ButtonLink";
import { Button } from "../../Common/Buttons/Button";
import FullPageLoadingScreen from "../../Common/FullPageLoadingScreen";
import { SettingsPanel } from "../../Common/Settings";
import { Body } from "../../Common/Typography";
import MultiSelect from "../../Inputs/MultiSelect";
import {
  Checkbox,
  Datepicker,
  Number,
  Select,
  Text,
} from "../../Inputs/react-hook-form";
import { ACCOUNT_INFO } from "./__queries__/general";
import FeatureFlags from "./FeatureFlags";
interface DroppedFile extends File {
  path?: string; //only marked optional to appease unavoidable, too strict type in useDropzone
}

interface FormStructure {
  accountName: string;
  initialFIRMDate: Maybe<string>;
  coastalADetermination: CoastalADetermination;
  datumConversionOffsetPurpose: DatumConversionOffsetPurpose;
  datumConversionOffset: Maybe<number>;
  repetitiveLossEnabled: boolean;
}

const GeneralSettingsForm = ({
  account,
  useDropzone,
}: {
  account: NonNullable<GetAccountInfoQuery["account"]>;
  useDropzone: (
    args: NonNullable<Parameters<typeof useReactDropzone>[0]>,
  ) => ReturnType<typeof useReactDropzone>;
}) => {
  const { admin, authorized } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const { register, handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      accountName: account.name,
      initialFIRMDate: account.initialFIRMDate,
      coastalADetermination: account.coastalADetermination,
      datumConversionOffsetPurpose: account.datumConversionOffsetPurpose,
      datumConversionOffset: account.datumConversionOffset,
      repetitiveLossEnabled: account.repetitiveLossEnabled,
    },
  });

  const [updateAccountMutation, { loading }] = useUpdateAccountMutation({
    onCompleted: () => {
      addSuccessToast("Your account settings were successfully updated.");
    },
    onError: () => {
      addErrorToast(
        "There was a problem updating your account settings. Please try again or contact support@withforerunner.com",
      );
    },
  });

  const canUpdateAccountInformation = authorized({
    resource: RESOURCE_NAME.ACCOUNT,
    permission: "update",
  });

  const [updateAccountLogo, { loading: logoMutationLoading }] =
    useUpdateAccountLogoMutation({
      refetchQueries: [{ query: ACCOUNT_INFO, variables: {} }],
      onCompleted: () => {
        addSuccessToast("Your account logo was successfully updated.");
      },
      onError: error => {
        addErrorToast(error.message);
      },
    });

  const [uploadFile, { loading: uploadLoading }] = useMultipartFileUpload({
    onError: () => {
      addErrorToast(
        "There was a problem uploading your logo. Please try again.",
      );
    },
  });

  const logoLoading = logoMutationLoading || uploadLoading;

  const onDropAccepted = (acceptedFiles: DroppedFile[]) => {
    const file = acceptedFiles[0]!;
    uploadFile({
      file,
      uploadType: MULTIPART_UPLOAD_TYPE.ACCOUNT_LOGO_UPLOAD,
      onCompleted: (key: string) => {
        void updateAccountLogo({ variables: { data: { logoKey: key } } });
      },
      onUploadError: () => {
        addErrorToast(
          "There was a problem uploading your logo. Please try again.",
        );
      },
    });
  };

  const { getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: {
      "image/*": [".png", ".jpeg", ".jpg"],
    },
    // Value is in bytes, max is 5mb
    maxSize: 1048576 * 5,
    multiple: false,
    onDropAccepted,
  });

  return (
    <SettingsPanel
      title="Account information"
      showInsufficientPermissionsBanner={!canUpdateAccountInformation}
    >
      <SettingsForm
        style={{ width: "100%" }}
        onSubmit={handleSubmit(async data => {
          const formData = omit(data, ["accountName"]);
          formData.initialFIRMDate =
            !isEmpty(formData.initialFIRMDate) ?
              formData.initialFIRMDate
            : null;
          track("Account Settings Updated");

          await updateAccountMutation({
            variables: { data: { id: account.id, ...formData } },
          });
        })}
      >
        <FlexColumn gap="m">
          <FlexRow gap="m">
            <AccountLogoOrFallbackIcon account={account} />
            <FlexRow gap="s">
              <input {...getInputProps()} />
              <FlexColumn gap="s">
                <FlexRow gap="s">
                  <Button
                    variant="outline"
                    size="small"
                    isDisabled={!canUpdateAccountInformation}
                    isPending={logoLoading}
                    onPress={() => {
                      open();
                      track("User uploaded a new logo");
                    }}
                    title="Upload image"
                  />
                  {account.logoUrl && (
                    <ButtonLink
                      styleVariant="critical"
                      size="small"
                      disabled={logoLoading}
                      onClick={() =>
                        updateAccountLogo({
                          variables: { data: { logoKey: null } },
                        })
                      }
                      leftIconName="trash-2"
                    >
                      Remove image
                    </ButtonLink>
                  )}
                </FlexRow>
                <Body color="contentSecondary" size="small" type="regular">
                  Logo must be a .jpg or .png and be below 5mb and have an
                  aspect ratio of 1:1.
                </Body>
              </FlexColumn>
            </FlexRow>
          </FlexRow>
          <Text
            disabled
            readonly
            label="Account name"
            size="medium"
            tooltip="If you would like to update your account name, please reach out to support@withforerunner.com"
            {...register("accountName")}
          />
        </FlexColumn>

        <SettingSection
          title="FIRM information"
          subtitle="Initial Flood Insurance Rate Map associated with this account."
        >
          <FlexRow gap="l" style={{ width: "100%" }}>
            <Datepicker
              control={control as unknown as Control<FieldValues, any>}
              name={"initialFIRMDate"}
              label={"Initial FIRM date"}
              disabled={!canUpdateAccountInformation}
            />
            {admin && (
              <Select
                label="Coastal A determination"
                options={[
                  {
                    label: "Disabled",
                    value: CoastalADetermination.DISABLED,
                  },
                  { label: "Manual", value: CoastalADetermination.MANUAL },
                  {
                    label: "Automatic",
                    value: CoastalADetermination.AUTOMATIC,
                  },
                ]}
                name="coastalADetermination"
                control={control}
                size="medium"
              />
            )}
          </FlexRow>
        </SettingSection>
        {admin && (
          <>
            <SettingSection
              title="Datum information"
              subtitle="Vertical reference used to measure and compare elevations."
            >
              <FlexRow gap="l" style={{ width: "calc(50% - 12px)" }}>
                <MultiSelect
                  disabled
                  label="Account datums"
                  // name is just here for type reasons, it's not actually part of the form
                  name="datums"
                  onChange={() => {}}
                  value={account.datums}
                  options={account.datums.map(datum => ({
                    label: DATUM_DISPLAY[datum].short,
                    value: datum,
                  }))}
                />
              </FlexRow>
              <FlexRow gap="l" style={{ width: "100%" }}>
                <Select
                  label="Datum conversion offset purpose"
                  options={[
                    {
                      label: "Fallback",
                      value: DatumConversionOffsetPurpose.FALLBACK,
                    },
                    {
                      label: "Override",
                      value: DatumConversionOffsetPurpose.OVERRIDE,
                    },
                  ]}
                  name="datumConversionOffsetPurpose"
                  size="medium"
                  control={control}
                />
                <Number
                  name="datumConversionOffset"
                  label="Datum conversion offset"
                  register={register}
                  step="any"
                />
              </FlexRow>
            </SettingSection>
            <SettingSection
              title="Sensitive data"
              subtitle="Fields that may affect compliance and reporting requirements."
            >
              <Checkbox
                name="repetitiveLossEnabled"
                control={control}
                label="Enable Repetitive Loss"
              />
            </SettingSection>
          </>
        )}
        <Button
          variant="primary"
          size="medium"
          type="submit"
          isDisabled={!canUpdateAccountInformation}
          isPending={loading}
          title="Update"
        />
      </SettingsForm>
    </SettingsPanel>
  );
};

const GeneralSettings = ({
  useDropzone = useReactDropzone,
}: {
  useDropzone?: (
    args: NonNullable<Parameters<typeof useReactDropzone>[0]>,
  ) => ReturnType<typeof useReactDropzone>;
}) => {
  const { authorized } = useContext(AuthContext);
  const { loading, data, error } = useGetAccountInfoQuery();

  if (loading || !data) return <FullPageLoadingScreen />;

  const account = data.account;
  if (error || !account) return <div>Error loading account.</div>;

  return (
    <div style={{ display: "flex", gap: "10px", flexDirection: "column" }}>
      <GeneralSettingsForm account={account} useDropzone={useDropzone} />
      {authorized({
        resource: RESOURCE_NAME.FEATURE_FLAG,
        permission: "update",
      }) && <FeatureFlags />}
    </div>
  );
};

export default GeneralSettings;

const SettingSection = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) => {
  return (
    <FlexColumn gap="m" style={{ width: "100%" }}>
      <FlexColumn gap="xs">
        <Body size="large" type="emphasis">
          {title}
        </Body>
        <Body size="default" type="regular" color="contentSecondary">
          {subtitle}
        </Body>
      </FlexColumn>
      {children}
    </FlexColumn>
  );
};
